/** @jsxImportSource theme-ui */
import { Box, Label, Text, Textarea } from 'theme-ui';
import { useState } from 'react';
import { toast } from 'react-toastify';
import Button from '@/components/Button';
import { ConfirmationModal } from '@/components/ConfirmationModal';
import { useCancelCompanyDeletionScheduleMutation } from '@/store/apiService';

interface Props {
    companyId: string;
    variant?: 'primary' | 'secondary' | 'danger';
}

export const CancelDeletionScheduleButton = ({ companyId, variant = 'secondary' }: Props) => {
    const [confirmVisible, setConfirmVisible] = useState(false);
    const [cancelReason, setCancelReason] = useState('');
    const [cancelSchedule, cancelState] = useCancelCompanyDeletionScheduleMutation();

    const close = () => {
        setConfirmVisible(false);
        setCancelReason('');
    };

    const onConfirm = () => {
        if (!cancelReason.trim()) {
            toast.error('A reason for cancelling is required', { autoClose: 5000 });
            return;
        }
        cancelSchedule({ CompanyId: companyId, CancelReason: cancelReason.trim() })
            .unwrap()
            .then((result) => {
                close();
                toast.success('Scheduled deletion cancelled', { autoClose: 5000 });
                (result?.Warnings ?? []).forEach((warning) =>
                    toast.warn(warning, { autoClose: 8000 })
                );
            })
            .catch((err) => {
                toast.error(
                    err?.data?.ResponseStatus?.Message ?? err?.message ?? 'Cancelling failed',
                    { autoClose: 8000 }
                );
            });
    };

    return (
        <>
            {confirmVisible ? (
                <ConfirmationModal
                    hideOnClickOutside
                    loading={cancelState.isLoading}
                    headingText="Cancel scheduled deletion"
                    yesText="Cancel deletion"
                    noText="Keep"
                    content={
                        <Box>
                            <Text as="p" mb={2}>
                                The company will not be deleted. Licenses that were ended when the
                                company was marked are not reactivated.
                            </Text>
                            <Label mb={1}>Reason</Label>
                            <Textarea
                                aria-label="Cancel reason"
                                rows={3}
                                value={cancelReason}
                                onChange={(e) => setCancelReason(e.target.value)}
                            />
                        </Box>
                    }
                    onNo={close}
                    onYes={onConfirm}
                />
            ) : null}
            <Button variant={variant} onClick={() => setConfirmVisible(true)}>
                Cancel scheduled deletion
            </Button>
        </>
    );
};
